// Copyright 2025, University of Maryland, All Rights Reserved
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  cn,
} from '@/components/ui';
import { toast } from 'sonner';
import { UserPlus, Search, Shield, User as UserIcon, Trash2, Pencil } from 'lucide-react';
import { useUsersApi, type UsersApi } from '../../services/users-api';
import type { UserProfileResponse } from '../../types/user';
import { AddUserDialog } from './AddUserDialog';
import { UserRemovalDialog } from './UserRemovalDialog';
import { EditOrgRolesDialog } from '../organizations/EditOrgRolesDialog';
import { AppConfigProvider } from '../../context/AppConfigContext';
import { getWorkspacesApi } from '../../services/api';
import {
  getConnectorAdminGrantOptions,
  listConnectorAdminGrants,
  connectorProviderLabel,
  type ConnectorAdminGrantsClient,
} from '@/subsystems/vault';

interface UserManagementProps {
  app_id: string;
  org_id?: string;
  className?: string;
  debug?: boolean;
  /** Optional API instance for testing */
  usersApi?: UsersApi;
  connectorGrantsApi?: ConnectorAdminGrantsClient;
}

interface UserManagementInnerProps {
  org_id?: string;
  className?: string;
  usersApi?: UsersApi;
  connectorGrantsApi?: ConnectorAdminGrantsClient;
}

interface OrgUserRow extends UserProfileResponse {
  roles?: string[];
}

const UserManagementInner: React.FC<UserManagementInnerProps> = ({
  org_id,
  className,
  usersApi: injectedApi,
  connectorGrantsApi: injectedConnectorGrantsApi,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [rolesDialogUser, setRolesDialogUser] = useState<OrgUserRow | null>(null);
  const [removalUserId, setRemovalUserId] = useState<string | null>(null);

  const queryClient = useQueryClient();
  const defaultApi = useUsersApi();
  const usersApi = injectedApi ?? defaultApi;
  const connectorGrantsApi = injectedConnectorGrantsApi ?? getWorkspacesApi();
  const isOrgMode = !!org_id;

  // Fetch users in scope (all users, or users for a specific org)
  const { data: scopeUsers, isLoading, isError } = useQuery<OrgUserRow[]>({
    queryKey: org_id ? ['users', org_id] : ['users', 'all'],
    queryFn: () => org_id ? usersApi.getOrgUsers(org_id) : usersApi.getAllUsers(),
  });

  const connectorGrants = useQuery({
    queryKey: ['connector-admin-grants', org_id],
    queryFn: () => listConnectorAdminGrants(connectorGrantsApi, org_id!),
    enabled: isOrgMode,
  });
  const connectorGrantOptions = useQuery({
    queryKey: ['connector-admin-grant-options', org_id],
    queryFn: () => getConnectorAdminGrantOptions(connectorGrantsApi, org_id!),
    enabled: isOrgMode,
  });

  // Organization administration must not depend on the global user directory.
  // Org admins can enter a new or existing directory ID directly; only the
  // global-admin view may enumerate system users and global authority.
  const allUserIds = React.useMemo(
    () => org_id ? [] : (scopeUsers ?? []).map(user => user.userId),
    [org_id, scopeUsers],
  );

  const createUserMutation = useMutation({
    mutationFn: ({ userId }: { userId: string }) => usersApi.createUser(userId),
    onSuccess: (createdUser) => {
      queryClient.setQueryData<OrgUserRow[]>(['users', 'all'], (current = []) => (
        [...current, createdUser].sort((a, b) => a.userId.localeCompare(b.userId))
      ));
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success(`${createdUser.userId} added`);
      setAddDialogOpen(false);
    },
    onError: (error) => {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create user';
      toast.error('Error', { description: errorMessage });
    },
  });

  const addToOrgMutation = useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: string }) =>
      usersApi.addUserToOrg(org_id!, userId, [role]),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success('User added to organization');
      setAddDialogOpen(false);
    },
    onError: (error) => {
      const errorMessage = error instanceof Error ? error.message : 'Failed to add user';
      toast.error('Error', { description: errorMessage });
    },
  });

  const updateUserMutation = useMutation({
    mutationFn: (data: { userId: string; isGlobalAdmin?: boolean }) =>
      usersApi.updateUser(data),
    onMutate: async (data) => {
      await queryClient.cancelQueries({ queryKey: ['users', 'all'] });
      const previous = queryClient.getQueryData<UserProfileResponse[]>(['users', 'all']);
      queryClient.setQueryData<UserProfileResponse[]>(['users', 'all'], (current = []) => (
        current.map((user) => user.userId === data.userId
          ? { ...user, isGlobalAdmin: data.isGlobalAdmin }
          : user)
      ));
      return { previous };
    },
    onError: (error, _data, context) => {
      queryClient.setQueryData(['users', 'all'], context?.previous);
      const errorMessage = error instanceof Error ? error.message : 'Failed to update user';
      toast.error('Error', { description: errorMessage });
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['users', 'all'] }),
  });

  const deleteUserMutation = useMutation({
    mutationFn: (userId: string) => org_id
      ? usersApi.removeUserFromOrg(org_id, userId)
      : usersApi.deleteUser(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success(org_id ? 'User removed from organization' : 'User deleted');
      setRemovalUserId(null);
    },
    onError: (error: unknown) => {
      const axiosError = error as { response?: { data?: { detail?: string } } };
      const errorMessage = axiosError.response?.data?.detail
        ?? (error instanceof Error ? error.message : 'Failed to delete user');
      toast.error('Cannot delete user', { description: errorMessage });
    },
  });

  const filteredUsers = React.useMemo<OrgUserRow[]>(() => {
    if (!scopeUsers) return [];
    if (!searchQuery) return scopeUsers;
    const query = searchQuery.toLowerCase();
    return scopeUsers.filter(u => u.userId.toLowerCase().includes(query));
  }, [scopeUsers, searchQuery]);

  const scopeUserIds = React.useMemo(() => (scopeUsers ?? []).map(u => u.userId), [scopeUsers]);
  const rolesDialogConnectorProviders = React.useMemo(
    () => (connectorGrants.data ?? [])
      .filter(grant => grant.userId === rolesDialogUser?.userId)
      .map(grant => grant.provider),
    [connectorGrants.data, rolesDialogUser?.userId],
  );

  const handleAddSubmit = (data: { userId: string; role?: string }) => {
    if (org_id) {
      addToOrgMutation.mutate({ userId: data.userId, role: data.role ?? 'member' });
    } else {
      createUserMutation.mutate({ userId: data.userId });
    }
  };

  const addPending = isOrgMode ? addToOrgMutation.isPending : createUserMutation.isPending;

  return (
    <div data-testid="user-management" className={cn('user-management', className)}>
      <div className="space-y-5">
        <div className="flex items-center justify-between">
          <p className="text-[15px] text-[#64748b]" data-testid="admin-users-heading">
            {isOrgMode ? 'Manage users in this organization' : 'Manage all users in the system'}
          </p>
          <Button data-testid="user-add-button" className="h-[42px] rounded-[10px] bg-[#1e293b] px-[18px] text-sm font-semibold text-white shadow-[0_1px_2px_rgba(0,0,0,0.12)] transition-[background,transform] duration-150 hover:bg-[#0f172a] active:scale-[0.97] has-[>svg]:px-[18px]" onClick={() => setAddDialogOpen(true)}>
            <UserPlus className="size-[17px]" />
            Add User
          </Button>
        </div>

        <div className="w-full">
          <div className="flex h-[46px] items-center gap-[11px] rounded-[10px] border border-[#e2e8f0] bg-white px-[14px] text-sm shadow-[0_1px_2px_rgba(0,0,0,0.04)] transition-[border-color,box-shadow] duration-[120ms] focus-within:border-[#93c5fd] focus-within:shadow-[0_0_0_3px_rgba(37,99,235,0.08)]">
            <Search className="size-[17px] shrink-0 text-[#94a3b8]" />
            <input
              data-testid="user-search"
              type="search"
              placeholder="Search users…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="flex-1 bg-transparent text-[15px] leading-[normal] text-[#0f172a] outline-none placeholder:text-[#94a3b8]"
            />
          </div>
        </div>

        {isLoading ? (
          <div className="text-center py-8">Loading users...</div>
        ) : isError ? (
          <div className="text-center py-8 text-destructive" data-testid="user-load-error">
            Unable to load users. Please try again.
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground" data-testid="user-no-results">
            {searchQuery ? 'No users found matching your search.' : 'No users found.'}
          </div>
        ) : (
          <div data-testid="user-list-card" className="overflow-hidden rounded-[14px] border border-[#e2e8f0] bg-white shadow-[0_1px_3px_rgba(0,0,0,0.06)]">
            <div className="px-6 pb-1 pt-5">
              <h2 className="text-lg font-bold leading-[normal] text-[#0f172a]">Users</h2>
              <p className="mt-0.5 text-[13.5px] leading-[normal] text-[#64748b]">
                {filteredUsers.length} user{filteredUsers.length !== 1 ? 's' : ''} found
              </p>
            </div>
            <Table className="mt-3">
                  <TableHeader>
                    <TableRow className="hover:bg-transparent">
                      <TableHead className="h-10 px-6 text-xs font-semibold text-[#475569]">User</TableHead>
                      {!isOrgMode && <TableHead className="h-10 w-[140px] px-4 text-center text-xs font-semibold text-[#475569]">Global Admin</TableHead>}
                      {isOrgMode && <TableHead className="h-10 px-4 text-xs font-semibold text-[#475569]">Roles</TableHead>}
                      <TableHead className="h-10 w-[90px] px-6 text-right text-xs font-semibold text-[#475569]">Delete</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredUsers.map((user, index) => {
                      const isGlobalAdmin = !!user.isGlobalAdmin;
                      const userConnectorProviders = (connectorGrants.data ?? [])
                        .filter(grant => grant.userId === user.userId)
                        .map(grant => grant.provider);
                      return (
                        <TableRow
                          key={user.userId}
                          data-testid={`user-row-${user.userId}`}
                          className="docreview-user-row hover:bg-[#fafbfc]"
                          style={{ animationDelay: `${Math.min(index, 14) * 22}ms` }}
                        >
                          <TableCell className="px-6 py-[13px] font-medium">
                            <div className="flex items-center gap-[11px]">
                              <UserIcon className="size-[17px] text-[#94a3b8]" />
                              <div className="text-[14.5px] font-normal text-[#0f172a]">{user.userId}</div>
                            </div>
                          </TableCell>
                          {!isOrgMode && (
                            <TableCell className="px-4 py-[13px] text-center">
                              <button
                                type="button"
                                role="switch"
                                aria-checked={isGlobalAdmin}
                                aria-label={`Toggle global admin for ${user.userId}`}
                                data-testid={`user-global-admin-switch-${user.userId}`}
                              className="docreview-user-switch disabled:cursor-not-allowed disabled:opacity-50"
                                data-state={isGlobalAdmin ? 'checked' : 'unchecked'}
                                onClick={() =>
                                  updateUserMutation.mutate({ userId: user.userId, isGlobalAdmin: !isGlobalAdmin })
                                }
                                disabled={updateUserMutation.isPending}
                              >
                                <span data-slot="switch-thumb" />
                              </button>
                            </TableCell>
                          )}
                          {isOrgMode && (
                            <TableCell className="px-4 py-[13px]">
                              <button
                                type="button"
                                onClick={() => setRolesDialogUser(user)}
                                aria-label={`Edit roles for ${user.userId}`}
                                className="group/roles -mx-1.5 flex flex-wrap items-center gap-1 rounded-lg border border-transparent px-1.5 py-1 text-left transition-colors hover:border-[#e2e8f0] hover:bg-accent"
                                data-testid={`user-roles-button-${user.userId}`}
                              >
                                {(user.roles ?? []).length === 0 ? (
                                  <span className="text-xs text-muted-foreground">No roles</span>
                                ) : (
                                  (user.roles ?? []).map((role) => (
                                    <Badge
                                      key={role}
                                      variant={role === 'admin' ? 'default' : 'secondary'}
                                      className="text-xs"
                                    >
                                      {role === 'admin' && <Shield className="h-3 w-3 mr-1" />}
                                      {/* Bare "admin"/"member" is ambiguous across the
                                          three authority levels; this list shows org roles. */}
                                      {role === 'admin'
                                        ? 'org admin'
                                        : role === 'member'
                                          ? 'org member'
                                          : role}
                                    </Badge>
                                  ))
                                )}
                                {userConnectorProviders.map(provider => (
                                  <Badge
                                    key={provider}
                                    variant="outline"
                                    className="text-xs"
                                    data-testid={`user-connector-role-${user.userId}-${provider}`}
                                  >
                                    {connectorProviderLabel(provider)} connector admin
                                  </Badge>
                                ))}
                                {/* cm-coding: The always-visible pencil marks the badge
                                    list as the role editor; hover alone was too easy to miss. */}
                                <Pencil className="size-3.5 shrink-0 text-[#94a3b8] transition-colors group-hover/roles:text-[#475569]" />
                              </button>
                            </TableCell>
                          )}
                          <TableCell className="px-6 py-[13px] text-right">
                            <Button
                              variant="ghost"
                              size="icon-sm"
                              aria-label={`Remove ${user.userId}`}
                              data-testid={`user-delete-${user.userId}`}
                              onClick={() => setRemovalUserId(user.userId)}
                              className="size-[34px] rounded-lg text-red-500 transition-[background,transform] duration-[120ms] hover:bg-red-50 hover:text-red-600 active:scale-90"
                            >
                              <Trash2 className="size-[17px]" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
          </div>
        )}
      </div>

      <AddUserDialog
        open={addDialogOpen}
        onOpenChange={setAddDialogOpen}
        scope={isOrgMode ? 'org' : 'all'}
        allUserIds={allUserIds}
        scopeUserIds={scopeUserIds}
        isPending={addPending}
        onSubmit={handleAddSubmit}
      />

      {isOrgMode && rolesDialogUser && org_id && (
        <EditOrgRolesDialog
          orgId={org_id}
          userId={rolesDialogUser.userId}
          initialRoles={rolesDialogUser.roles ?? []}
          usersApi={usersApi}
          connectorProviders={connectorGrantOptions.data?.providers ?? []}
          initialConnectorProviders={rolesDialogConnectorProviders}
          connectorRolesLoading={connectorGrants.isPending || connectorGrantOptions.isPending}
          connectorRolesError={connectorGrants.isError || connectorGrantOptions.isError}
          connectorGrantsApi={connectorGrantsApi}
          open={!!rolesDialogUser}
          onOpenChange={(open) => {
            if (!open) setRolesDialogUser(null);
          }}
        />
      )}

      <UserRemovalDialog
        userId={removalUserId}
        scope={isOrgMode ? 'organization' : 'all'}
        isPending={deleteUserMutation.isPending}
        onOpenChange={(open) => {
          if (!open && !deleteUserMutation.isPending) setRemovalUserId(null);
        }}
        onConfirm={() => {
          if (removalUserId) deleteUserMutation.mutate(removalUserId);
        }}
      />

    </div>
  );
};

export const UserManagement: React.FC<UserManagementProps> = ({
  app_id,
  org_id,
  className,
  debug = false,
  usersApi,
  connectorGrantsApi,
}) => {
  return (
    <AppConfigProvider app_id={app_id} debug={debug}>
      <UserManagementInner
        org_id={org_id}
        className={className}
        usersApi={usersApi}
        connectorGrantsApi={connectorGrantsApi}
      />
    </AppConfigProvider>
  );
};
